import { RefObject, useCallback, useEffect, useState } from 'react';

export type TableHorizontalScroll = {
  scroller: HTMLElement | null;
  scrollWidth: number;
  areaTop: number;
  areaHeight: number;
  screenLeft: number;
  screenWidth: number;
  canScrollRight: boolean;
  isScrollbarOffScreen: boolean;
};

const EMPTY: TableHorizontalScroll = {
  scroller: null,
  scrollWidth: 0,
  areaTop: 0,
  areaHeight: 0,
  screenLeft: 0,
  screenWidth: 0,
  canScrollRight: false,
  isScrollbarOffScreen: false
};

// react-data-table-component wraps the table in an element that scrolls it
// sideways. It has no class of its own, so find it by its overflow.
export const findScroller = (container: HTMLElement | null) => {
  let element = container?.querySelector<HTMLElement>('.rdt_Table') ?? null;

  while (element && element !== container) {
    if (/auto|scroll/.test(getComputedStyle(element).overflowX)) {
      return element;
    }
    element = element.parentElement;
  }

  return null;
};

const isSame = (a: TableHorizontalScroll, b: TableHorizontalScroll) =>
  a.scroller === b.scroller &&
  a.scrollWidth === b.scrollWidth &&
  a.areaTop === b.areaTop &&
  a.areaHeight === b.areaHeight &&
  a.screenLeft === b.screenLeft &&
  a.screenWidth === b.screenWidth &&
  a.canScrollRight === b.canScrollRight &&
  a.isScrollbarOffScreen === b.isScrollbarOffScreen;

export const useTableHorizontalScroll = (
  containerRef: RefObject<HTMLElement>,
  rerenderKey: unknown
): TableHorizontalScroll => {
  const [state, setState] = useState<TableHorizontalScroll>(EMPTY);

  const measure = useCallback(() => {
    const container = containerRef.current;
    const scroller = findScroller(container);

    if (!container || !scroller) {
      setState((previous) => (isSame(previous, EMPTY) ? previous : EMPTY));
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const rect = scroller.getBoundingClientRect();
    const overflows = scroller.scrollWidth > scroller.clientWidth + 1;

    const next: TableHorizontalScroll = {
      scroller,
      scrollWidth: scroller.scrollWidth,
      areaTop: rect.top - containerRect.top,
      areaHeight: rect.height,
      screenLeft: rect.left,
      screenWidth: rect.width,
      canScrollRight:
        overflows &&
        scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1,
      isScrollbarOffScreen:
        overflows &&
        rect.top < window.innerHeight &&
        rect.bottom > window.innerHeight
    };

    // Most scroll events change nothing here, and keeping the same state
    // spares the table a re-render on every frame.
    setState((previous) => (isSame(previous, next) ? previous : next));
  }, [containerRef]);

  useEffect(() => {
    const scroller = findScroller(containerRef.current);
    const table = scroller?.querySelector('.rdt_Table');

    let frame = 0;
    const measureOncePerFrame = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        measure();
      });
    };

    measure();

    const resizeObserver = new ResizeObserver(measureOncePerFrame);
    if (scroller) resizeObserver.observe(scroller);
    if (table) resizeObserver.observe(table);

    scroller?.addEventListener('scroll', measureOncePerFrame, {
      passive: true
    });
    window.addEventListener('scroll', measureOncePerFrame, { passive: true });
    window.addEventListener('resize', measureOncePerFrame);

    return () => {
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      scroller?.removeEventListener('scroll', measureOncePerFrame);
      window.removeEventListener('scroll', measureOncePerFrame);
      window.removeEventListener('resize', measureOncePerFrame);
    };
  }, [containerRef, measure, rerenderKey]);

  return state;
};
